import { useQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../utils/api-handler.util'
import { useAuth } from '../../contexts/AuthContext'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import {
  PAYMENT_EXPIRY_KEY,
  PAYMENT_ID_KEY,
  PAYMENT_REFETCH_AFTER_MILLISECONDS,
  paymentStatus,
} from '../../constants/payment'
import { XMarkIcon, ArrowPathIcon, CheckIcon, ClockIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import CurrencyDisplay from '../common/CurrencyDisplay'
import useRetryPayment from '../../hooks/useRetryPayment'

const VerificationPaymentNotification = ({ onRetryPayment }) => {
  const { userType, refresh, currentUser } = useAuth()
  const paymentId = localStorage.getItem(PAYMENT_ID_KEY)
  const paymentExpiry = localStorage.getItem(PAYMENT_EXPIRY_KEY)
  const hasExistingPayment = () => paymentId && paymentExpiry && Date.now() <= Number(paymentExpiry)
  const [shouldRefetch, setShouldRefetch] = useState(hasExistingPayment())

  const { data: paymentData } = useQuery({
    queryKey: QueryKeys.paymentDetails(paymentId),
    queryFn: () => handleGetRequest(`/${userType}s/my-payments/${paymentId}`),
    refetchInterval: PAYMENT_REFETCH_AFTER_MILLISECONDS,
    enabled: shouldRefetch && !!paymentId,
  })

  const payment = paymentData?.data
  const { canRetry, isCountingDown, formatTime } = useRetryPayment(payment?.createdAt)

  useEffect(() => {
    setShouldRefetch(payment?.status === paymentStatus.PENDING)
  }, [payment?.status])

  if (paymentExpiry && Date.now() > Number(paymentExpiry)) {
    localStorage.removeItem(PAYMENT_ID_KEY)
    localStorage.removeItem(PAYMENT_EXPIRY_KEY)
    return null
  }

  const handleRetryPayment = () => {
    localStorage.removeItem(PAYMENT_ID_KEY)
    localStorage.removeItem(PAYMENT_EXPIRY_KEY)
    onRetryPayment()
  }

  const renderFailed = () => {
    if (payment.status !== paymentStatus.FAILED) return
    return (
      <div className="flex flex-col items-center text-center p-6">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <XMarkIcon className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-xl font-bold text-red-700 mb-2">Payment Failed</h2>
        <p className="text-gray-600 mb-6">Your verification payment could not be processed.</p>
        <button
          onClick={handleRetryPayment}
          className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
        >
          <ArrowPathIcon className="w-4 h-4 inline mr-2" />
          Retry Payment
        </button>
      </div>
    )
  }

  const renderPending = () => {
    if (payment.status !== paymentStatus.PENDING) return
    return (
      <div className="flex flex-col items-center text-center">
        <div className="relative w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
          <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <h2 className="text-xl font-bold text-yellow-700 mb-2">
          {canRetry ? 'Payment Timeout' : 'Payment Processing'}
        </h2>
        <p className="text-gray-600 text-sm max-w-md mb-4">
          {canRetry
            ? 'Your payment is taking longer than expected. You can retry the payment now.'
            : 'We have initiated your payment. You should receive an M-Pesa prompt, please enter your PIN to complete the transaction.'}
        </p>

        {isCountingDown && (
          <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-center justify-center gap-2 text-yellow-700">
              <ClockIcon className="w-4 h-4" />
              <span className="text-sm font-medium">
                Retry in: <span className="font-mono text-lg">{formatTime()}</span>
              </span>
            </div>
          </div>
        )}

        {canRetry ? (
          <button
            onClick={handleRetryPayment}
            className="px-8 py-3 bg-yellow-500 text-white rounded-xl hover:bg-yellow-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Retry Payment
          </button>
        ) : (
          <div className="flex items-center space-x-2 text-yellow-600"></div>
        )}
      </div>
    )
  }

  const removeFromLocalAndClear = async () => {
    localStorage.removeItem(PAYMENT_ID_KEY)
    localStorage.removeItem(PAYMENT_EXPIRY_KEY)
    await refresh()
    QueryKeys.shopVerificationDetails.invalidate(currentUser.id)
  }

  const renderCompleted = () => {
    if (payment?.status !== paymentStatus.COMPLETED) return
    removeFromLocalAndClear()
    return (
      <div className="flex flex-col items-center text-center p-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckIcon className="w-8 h-8 text-green-500" />
        </div>
        <h2 className="text-xl font-bold text-green-700 mb-2">Payment Successful!</h2>
        <div className="text-gray-600 space-y-2 mb-6">
          <p>
            Amount: <CurrencyDisplay amount={payment?.amount} />
          </p>
          <p>Date: {new Date(payment?.createdAt).toLocaleString()}</p>
        </div>
      </div>
    )
  }

  if (!payment) return null

  return (
    <div className="bg-background rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
      {renderPending()}
      {renderFailed()}
      {renderCompleted()}
    </div>
  )
}

export default VerificationPaymentNotification
